import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config/index.js';

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success.
 * - Throws an Error carrying { status, details } on failure, so callers can
 *   `try/catch` and show err.message.
 */
class ApiService {
  async request(endpoint, { method = 'GET', body, headers } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    return payload?.data ?? payload;
  }

  // ---- Auth ----
  register({ email, name, password }) {
    return this.request('/auth/register', { method: 'POST', body: { email, name, password } });
  }

  login({ email, password }) {
    return this.request('/auth/login', { method: 'POST', body: { email, password } });
  }

  me() {
    return this.request('/auth/me');
  }

  // ---- Users ----
  listUsers({ limit = 50, offset = 0 } = {}) {
    return this.request(`/users?limit=${limit}&offset=${offset}`);
  }

  // ---- Portfolio Knowledge Map (owner-partitioned) ----
  portfolioKpis({ range = '12m' } = {}) {
    return this.request(`/portfolio/kpis?range=${encodeURIComponent(range)}`);
  }

  portfolioRentIndex() {
    return this.request('/portfolio/rent-index');
  }

  portfolioRenewalFunnel() {
    return this.request('/portfolio/renewal-funnel');
  }

  portfolioBuildings({ search = '', community = '' } = {}) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (community) params.set('community', community);
    const qs = params.toString();
    return this.request(`/portfolio/buildings${qs ? `?${qs}` : ''}`);
  }

  portfolioBuildingDetail(id) {
    return this.request(`/portfolio/buildings/${encodeURIComponent(id)}`);
  }

  // ---- RERA Compliance Rule Engine ----
  complianceRules() {
    return this.request('/compliance/rules');
  }

  complianceIndexFeed() {
    return this.request('/compliance/index-feed');
  }

  complianceCertificates({ search = '', verdict = '' } = {}) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (verdict) params.set('verdict', verdict);
    const qs = params.toString();
    return this.request(`/compliance/certificates${qs ? `?${qs}` : ''}`);
  }

  complianceCertificate(hash) {
    return this.request(`/compliance/certificates/${encodeURIComponent(hash)}`);
  }

  complianceEvaluate(body) {
    return this.request('/compliance/evaluate', { method: 'POST', body });
  }

  complianceOverrides({ status = '' } = {}) {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request(`/compliance/overrides${qs}`);
  }

  complianceCreateOverride(body) {
    return this.request('/compliance/overrides', { method: 'POST', body });
  }

  complianceAuthorizeOverride(id, pin) {
    return this.request(`/compliance/overrides/${encodeURIComponent(id)}/authorize`, {
      method: 'POST',
      body: { pin },
    });
  }

  complianceRejectOverride(id) {
    return this.request(`/compliance/overrides/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
    });
  }

  // ---- Notice & Communication Studio ----
  notices() {
    return this.request('/notices');
  }

  noticeDraft(id) {
    return this.request(`/notices/${encodeURIComponent(id)}`);
  }

  verifyNotice(id, slots) {
    return this.request(`/notices/${encodeURIComponent(id)}/verify`, { method: 'POST', body: { slots } });
  }

  dispatchNotice(id, slots) {
    return this.request(`/notices/${encodeURIComponent(id)}/dispatch`, { method: 'POST', body: { slots } });
  }

  noticeApprovals() {
    return this.request('/notices/approvals');
  }

  approveNotice(id, note) {
    return this.request(`/notices/${encodeURIComponent(id)}/approve`, { method: 'POST', body: { note } });
  }

  requestNoticeEdits(id, note) {
    return this.request(`/notices/${encodeURIComponent(id)}/request-edits`, { method: 'POST', body: { note } });
  }

  escalateNotice(id, note) {
    return this.request(`/notices/${encodeURIComponent(id)}/escalate`, { method: 'POST', body: { note } });
  }

  // ---- Commercial Ledger & Billing (Outcome Attribution Ledger) ----
  billingLedger({ driver = '', status = '', landlord = '', month = '' } = {}) {
    const params = new URLSearchParams();
    if (driver) params.set('driver', driver);
    if (status) params.set('status', status);
    if (landlord) params.set('landlord', landlord);
    if (month) params.set('month', month);
    const qs = params.toString();
    return this.request(`/billing/ledger${qs ? `?${qs}` : ''}`);
  }

  billingTrace(txRef) {
    return this.request(`/billing/ledger/${encodeURIComponent(txRef)}/trace`);
  }

  billingReconcile(txRef) {
    return this.request(`/billing/ledger/${encodeURIComponent(txRef)}/reconcile`, { method: 'POST' });
  }

  billingDispute(txRef, reason) {
    return this.request(`/billing/ledger/${encodeURIComponent(txRef)}/dispute`, { method: 'POST', body: { reason } });
  }

  billingStatement(body) {
    return this.request('/billing/statements', { method: 'POST', body });
  }

  // ---- Tenant Renewal Portal ----
  tenantRenewal() {
    return this.request('/tenant/renewal');
  }

  tenantAccept() {
    return this.request('/tenant/renewal/accept', { method: 'POST' });
  }

  tenantNegotiate(body) {
    return this.request('/tenant/renewal/negotiate', { method: 'POST', body });
  }

  tenantDecline(body) {
    return this.request('/tenant/renewal/decline', { method: 'POST', body });
  }

  tenantExecute(body) {
    return this.request('/tenant/renewal/execute', { method: 'POST', body });
  }

  // ---- Integration Management Hub ----
  signatureConfig() {
    return this.request('/integrations/signature');
  }

  testSignatureConnection(body) {
    return this.request('/integrations/signature/test', { method: 'POST', body });
  }

  saveSignatureConfig(body) {
    return this.request('/integrations/signature', { method: 'POST', body });
  }

  pmsConnectors() {
    return this.request('/integrations/pms');
  }

  testPmsCredentials(body) {
    return this.request('/integrations/pms/test', { method: 'POST', body });
  }

  savePmsConnector(body) {
    return this.request('/integrations/pms', { method: 'POST', body });
  }

  syncJobs() {
    return this.request('/integrations/sync-jobs');
  }

  syncJobPayload(jobId) {
    return this.request(`/integrations/sync-jobs/${encodeURIComponent(jobId)}/payload`);
  }

  rerunSyncJob(jobId) {
    return this.request(`/integrations/sync-jobs/${encodeURIComponent(jobId)}/rerun`, { method: 'POST' });
  }

  triggerIndexCapture(community) {
    return this.request('/integrations/index-capture', { method: 'POST', body: { community } });
  }

  // ---- PMS ↔ Ejari data conflicts ----
  dataConflicts({ status = 'open' } = {}) {
    return this.request(`/conflicts?status=${encodeURIComponent(status)}`);
  }

  resolveDataConflict(id, body) {
    return this.request(`/conflicts/${encodeURIComponent(id)}/resolve`, { method: 'POST', body });
  }

  // ---- Yield Optimiser ----
  yieldContract({ vacancyDays, commissionRate, dailyBasis } = {}) {
    const params = new URLSearchParams();
    if (vacancyDays !== undefined) params.set('vacancyDays', vacancyDays);
    if (commissionRate !== undefined) params.set('commissionRate', commissionRate);
    if (dailyBasis !== undefined) params.set('dailyBasis', dailyBasis);
    const qs = params.toString();
    return this.request(`/yield/contract${qs ? `?${qs}` : ''}`);
  }

  yieldEvaluate(body) {
    return this.request('/yield/evaluate', { method: 'POST', body });
  }

  yieldSimulation() {
    return this.request('/yield/simulation');
  }

  yieldCommit(body) {
    return this.request('/yield/commit', { method: 'POST', body });
  }

  // ---- Owner Governance ----
  governanceOwners() {
    return this.request('/owner-governance/owners');
  }

  governanceMandate(ownerName) {
    return this.request(`/owner-governance/mandate?ownerName=${encodeURIComponent(ownerName)}`);
  }

  governanceSaveMandate(body) {
    return this.request('/owner-governance/mandate', { method: 'POST', body });
  }

  governanceReports() {
    return this.request('/owner-governance/reports');
  }

  governanceGenerateReport(body) {
    return this.request('/owner-governance/reports', { method: 'POST', body });
  }

  governanceCertifyReport(id) {
    return this.request(`/owner-governance/reports/${encodeURIComponent(id)}/certify`, {
      method: 'POST',
    });
  }

  governancePublishReport(id) {
    return this.request(`/owner-governance/reports/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
    });
  }

  governancePortal(ownerName) {
    return this.request(`/owner-governance/portal?ownerName=${encodeURIComponent(ownerName)}`);
  }

  governanceDownloadReport(reportId) {
    return this.request(`/owner-governance/reports/${encodeURIComponent(reportId)}/download`);
  }

  governanceDownloadCertificate(hash) {
    return this.request(`/owner-governance/certificate/${encodeURIComponent(hash)}/download`);
  }

  // ---- Renewal Radar ----
  escalationConfig() {
    return this.request('/renewals/escalation-config');
  }

  saveEscalationConfig(body) {
    return this.request('/renewals/escalation-config', { method: 'POST', body });
  }

  runEscalations() {
    return this.request('/renewals/escalation-config/run', { method: 'POST' });
  }

  escalationReceipts() {
    return this.request('/renewals/escalation-receipts');
  }

  renewals({ search = '', horizon = '' } = {}) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (horizon) params.set('horizon', horizon);
    const qs = params.toString();
    return this.request(`/renewals${qs ? `?${qs}` : ''}`);
  }

  createRenewal(body) {
    return this.request('/renewals', { method: 'POST', body });
  }

  /**
   * Downloads a certificate bundle (pdf|json) as a Blob, attaching the bearer
   * token. Returns the Blob so the caller can trigger a browser download.
   */
  async downloadCertificate(hash, format = 'pdf') {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(
      `${API_BASE_URL}/compliance/certificates/${encodeURIComponent(hash)}/${format}`,
      { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
    );
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    return res.blob();
  }
}

export default new ApiService();
